import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { AppRouter } from '.';
import { AuthProvider } from 'oidc-react';
import { authSettings } from '../api/OidcSettings';
import { SessionStore } from '../services/SessionStore';
import { UrlHelper, IsAppOpenedInIframe, IsAppOpenedByExtApp } from '../services';
import { EUrlParams, ESessionStore, ParamMappings } from '../data/Constants';
import type { IUrlParams } from '../../types';

/**
 * Stores URL parameters in session storage and optionally updates the URL.
 *
 * Iterates over a predefined set of parameter mappings, checks if each parameter exists in the provided
 * urlParams object, and if so, updates the session storage with the new value (converting currency to uppercase).
 * If the parameter is marked for deletion, it removes the parameter from the URL's search string and triggers navigation.
 *
 * @param {IUrlParams} urlParams - The URL parameters to store in session storage.
 * @param {function} navigate - The navigation function to update the URL if needed.
 */
function setInSessionStore(
  urlParams: IUrlParams,
  navigate: (path: object, replace: object) => void
) {
  let search = globalThis.location.search;
  let shouldNavigate = false;

  ParamMappings.forEach(({ key, urlKey, remove }) => {
    const value = urlParams[key as keyof IUrlParams];
    if (value) {
      //get the current session value for the key
      const current = SessionStore.get(key);
      const newValue = key === ESessionStore.Currency ? value.toUpperCase() : value;

      //set new value to the key
      if (current !== newValue) {
        SessionStore.set(key, newValue);
      }

      if (remove) {
        const newSearch = UrlHelper.deleteSearchParameter(urlKey, search);
        if (newSearch !== search) {
          search = newSearch;
          shouldNavigate = true;
        }
      }
    }
  });

  if (shouldNavigate) {
    navigate({ pathname: '/', search }, { replace: true });
  }
}

/**
 * Main component for authentication handling.
 * Skips authentication in developer mode
 * @returns {JSX.Element} the AppRouter component
 */
export const AuthComponent = () => {
  const configurationId = UrlHelper.getSearchParameter(EUrlParams.ConfigurationId);
  const scope = UrlHelper.getSearchParameter(EUrlParams.Scope);
  const token = UrlHelper.getSearchParameter(EUrlParams.Token);
  const language = UrlHelper.getSearchParameter(EUrlParams.Language);
  const country = UrlHelper.getSearchParameter(EUrlParams.Country);
  const currency = UrlHelper.getSearchParameter(EUrlParams.Currency);
  const headerLess = UrlHelper.getSearchParameter(EUrlParams.HeaderLess);

  const navigate = useNavigate();
  // To store configurationId, scope in Session Storage

  const [state, setState] = useState({
    configdata: {
      AuthorityUrl: '',
      ClientId: '',
      Redirect: '',
      Redirect_Silent: '',
      Developer_Mode: false,
    },
  });

  useEffect(() => {
    const urlParams: IUrlParams = {
      configurationId: configurationId,
      scope: scope,
      language: language,
      country: country,
      currency: currency,
      headerLess: headerLess,
      token: token,
    };
    setInSessionStore(urlParams, navigate);
    setState({
      ...state,
      configdata: authSettings,
    });
  }, []);

  useEffect(() => {
    if (configurationId) {
      SessionStore.set(ESessionStore.ConfigurationId, configurationId);
    }
  }, [configurationId]); //To update the session store on first render and whenever the config id changes(like new configutaion creation etc.,)

  const oidcConfig = {
    onSignIn: async () => {
      let params = '';
      if (SessionStore.get(ESessionStore.ConfigurationId)) {
        params = UrlHelper.createSearchParameter(
          EUrlParams.ConfigurationId,
          SessionStore.get(ESessionStore.ConfigurationId)
        );
      }
      // Replace history before setting viewId so all unrelated query params are removed
      navigate(
        {
          pathname: '/',
          search: params,
        },
        { replace: true }
      );
    },

    authority: state.configdata.AuthorityUrl,
    clientId: state.configdata.ClientId,
    automaticSilentRenew: true,
    grant_type: 'refresh_token',
    response_type: 'code token id_token',
    redirectUri: state.configdata.Redirect,
    silent_redirect_uri: state.configdata.Redirect_Silent,
    scope: state.configdata.ClientId + '/User.Read email openid profile',
    loadUserInfo: false,
    revokeAccessTokenOnSignout: true,
  };
  if (!oidcConfig.authority) {
    return null;
  }
  if (state.configdata.Developer_Mode || IsAppOpenedInIframe() || IsAppOpenedByExtApp()) {
    return <AppRouter />;
  }
  return (
    <AuthProvider {...oidcConfig}>
      <AppRouter />
    </AuthProvider>
  );
};
